import { useMemo } from 'react';

import { PENDING_TYPES, PENDING_TYPE_KEYS, todayStr } from '../utils/pending';

import './styles/pending-calendar.css';

// Calendario mensual de tareas pendientes (admin-kpis ?view=pending). Pinta un punto
// por TIPO (dx1/dx2/partos) en los días con eventos. `compact` = versión mini (Tareas);
// completo + seleccionable (Planeación). Los datos cubren la ventana del contrato
// (vencidos + próximos 7/14 días); meses fuera de esa ventana aparecen sin puntos.

const MON_FULL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const pad = (n) => String(n).padStart(2, '0');

function monthGrid(year, month) {
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = (first.getDay() + 6) % 7; // lunes = 0
    const cells = [];
    for (let i = 0; i < startWeekday; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(`${year}-${pad(month + 1)}-${pad(d)}`);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

function IconChevron({ dir }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={dir === 'prev' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
        </svg>
    );
}

export default function PendingCalendar({ events = [], year, month, onMonthChange, compact = false, selectedDate = null, onSelectDay, types = PENDING_TYPES, typeKeys = PENDING_TYPE_KEYS }) {
    const byDate = useMemo(() => {
        const m = new Map();
        events.forEach((e) => {
            if (!e.date) return;
            if (!m.has(e.date)) m.set(e.date, { types: new Set(), overdue: false });
            const slot = m.get(e.date);
            slot.types.add(e.type);
            if (e.bucket === 'overdue') slot.overdue = true;
        });
        return m;
    }, [events]);

    const cells = useMemo(() => monthGrid(year, month), [year, month]);
    const today = todayStr();
    const selectable = typeof onSelectDay === 'function';

    return (
        <div className={`pcal ${compact ? 'pcal--compact' : ''}`}>
            <div className="pcal__head">
                <button type="button" className="pcal__nav" onClick={() => onMonthChange?.(-1)} aria-label="Mes anterior"><IconChevron dir="prev" /></button>
                <span className="pcal__month">{MON_FULL[month]} {year}</span>
                <button type="button" className="pcal__nav" onClick={() => onMonthChange?.(1)} aria-label="Mes siguiente"><IconChevron dir="next" /></button>
            </div>

            <div className="pcal__grid pcal__grid--weekdays" aria-hidden="true">
                {WEEKDAYS.map((w, i) => <span key={i} className="pcal__weekday">{w}</span>)}
            </div>

            <div className="pcal__grid">
                {cells.map((date, i) => {
                    if (!date) return <span key={`b${i}`} className="pcal__cell pcal__cell--blank" />;
                    const slot = byDate.get(date);
                    const day = Number(date.slice(8, 10));
                    const isToday = date === today;
                    const isSelected = date === selectedDate;
                    const classes = ['pcal__cell'];
                    if (isToday) classes.push('pcal__cell--today');
                    if (isSelected) classes.push('pcal__cell--selected');
                    if (slot) classes.push('pcal__cell--events');
                    if (slot?.overdue) classes.push('pcal__cell--overdue');

                    const content = (
                        <>
                            <span className="pcal__day">{day}</span>
                            {slot && (
                                <span className="pcal__dots">
                                    {typeKeys.filter((k) => slot.types.has(k)).map((k) => (
                                        <span key={k} className="pcal__dot" style={{ background: types[k].color }} />
                                    ))}
                                </span>
                            )}
                        </>
                    );

                    return selectable ? (
                        <button
                            key={date}
                            type="button"
                            className={classes.join(' ')}
                            onClick={() => onSelectDay(isSelected ? null : date)}
                            aria-pressed={isSelected}
                        >
                            {content}
                        </button>
                    ) : (
                        <span key={date} className={classes.join(' ')}>{content}</span>
                    );
                })}
            </div>

            <div className="pcal__legend">
                {typeKeys.map((k) => (
                    <span key={k} className="pcal__legend-item">
                        <span className="pcal__dot" style={{ background: types[k].color }} />
                        {types[k].label}
                    </span>
                ))}
            </div>
        </div>
    );
}
